'use client';

import {
    normalizeClientRuntimeIncident,
    reportClientRuntimeIncident,
    type ClientRuntimeBoundary,
} from '@/client/error-handling/client-runtime-incident';
import { RuntimeFailurePage } from '@/components/runtime-failure/runtime-failure-page';
import { useEffect, useMemo, useRef } from 'react';

type RuntimeFailureBoundaryProps = {
    error: Error & { digest?: string };
    reset: () => void;
    boundary: ClientRuntimeBoundary;
};

const buildIncidentKey = (error: Error & { digest?: string }, boundary: ClientRuntimeBoundary) => {
    return [boundary, error.name, error.message, error.digest ?? ''].join(':');
};

export const RuntimeFailureBoundary = ({ error, reset, boundary }: RuntimeFailureBoundaryProps) => {
    const incidentKey = buildIncidentKey(error, boundary);
    const incident = useMemo(
        () =>
            normalizeClientRuntimeIncident({
                error,
                boundary,
            }),
        [incidentKey],
    );
    const lastReportedFailureIdRef = useRef<string | null>(null);

    useEffect(() => {
        if (lastReportedFailureIdRef.current === incident.failureId) {
            return;
        }

        lastReportedFailureIdRef.current = incident.failureId;
        reportClientRuntimeIncident({
            incident,
            error,
        });
    }, [error, incident]);

    return <RuntimeFailurePage incident={incident} onRetry={reset} />;
};
